/* eslint-disable tailwindcss/no-custom-classname */
import type { MapLocation } from '@frontend/api-types';
import type { CommonModalProps } from '@frontend/web-react';
import { Modal, Text, XStack, YStack } from '@frontend/web-react';
import { captureSentryException } from '@frontend/web-utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PlaceDetailModal } from './PlaceDetailModal';
import { createIconMarker, createLabelMarker, getMarkerColor } from './markers';
import type { PlaceDetails } from './placesApi';
import { fetchPlaceDetails, fetchPlaceIcon } from './placesApi';

export type { MapLocation } from '@frontend/api-types';
export { PlaceDetailModal } from './PlaceDetailModal';
export { fetchPlaceDetails } from './placesApi';
export type { PlaceDetails } from './placesApi';

export type GoogleMapWidgetProps = {
  locations: MapLocation[];
};

type ValidMapLocation = MapLocation & { lat: number; lng: number };

const MAP_ID = 'google-map-widget';
const COMPACT_HEIGHT = 300;
const MODAL_HEIGHT = 'calc(90vh - 80px)';

const useMapRenderer = ({
  containerRef,
  gestureHandling = 'cooperative',
  locations,
  onMarkerClick,
}: {
  containerRef: React.RefObject<HTMLDivElement | null>;
  gestureHandling?: 'cooperative' | 'greedy';
  locations: ValidMapLocation[];
  onMarkerClick?: (location: ValidMapLocation, map: google.maps.Map) => void;
}) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<google.maps.marker.AdvancedMarkerElement[]>([]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || locations.length === 0) return;

    /* eslint-disable fp/no-let, fp/no-mutation, fp/no-mutating-methods */
    let cancelled = false;

    const map = new google.maps.Map(container, {
      disableDefaultUI: true,
      gestureHandling,
      mapId: MAP_ID,
      zoomControl: true,
    });
    mapRef.current = map;

    markersRef.current.forEach((m) => m.remove());
    markersRef.current = [];

    const bounds = new google.maps.LatLngBounds();
    const service = new google.maps.places.PlacesService(map);

    locations.forEach((loc, idx) => {
      const position = { lat: loc.lat, lng: loc.lng };
      bounds.extend(position);

      const pinEl = createLabelMarker(loc.label, getMarkerColor(idx));

      const marker = new google.maps.marker.AdvancedMarkerElement({
        content: pinEl,
        map,
        position,
        title: loc.label,
      });

      if (onMarkerClick) {
        marker.addListener('click', () => onMarkerClick(loc, map));
      }

      markersRef.current.push(marker);

      // Async upgrade: replace label with Place icon when available
      fetchPlaceIcon(service, loc.placeId).then((icon) => {
        if (cancelled || !icon) return;
        const iconEl = createIconMarker(icon.iconMaskUrl, icon.bgColor);
        marker.content = iconEl;
      });
    });

    map.fitBounds(bounds, { bottom: 40, left: 40, right: 40, top: 40 });

    return () => {
      cancelled = true;
      markersRef.current.forEach((m) => m.remove());
      markersRef.current = [];
      mapRef.current = null;
    };
    /* eslint-enable fp/no-let, fp/no-mutation, fp/no-mutating-methods */
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onMarkerClick is stable via useCallback
  }, [containerRef, gestureHandling, locations]);
};

const MapRenderer = ({
  gestureHandling,
  height,
  locations,
  onMarkerClick,
}: {
  gestureHandling?: 'cooperative' | 'greedy';
  height: number | string;
  locations: ValidMapLocation[];
  onMarkerClick?: (location: ValidMapLocation, map: google.maps.Map) => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  useMapRenderer({ containerRef, gestureHandling, locations, onMarkerClick });

  return <div ref={containerRef} style={{ borderRadius: 8, height, width: '100%' }} />;
};

const GoogleMapModal = ({
  isOpen,
  locations,
  onClose,
  onMarkerClick,
}: CommonModalProps & {
  locations: ValidMapLocation[];
  onMarkerClick?: (location: ValidMapLocation, map: google.maps.Map) => void;
}) => (
  <Modal isOpen={isOpen} onClose={onClose} padding="none" size="90vw" title="Map View">
    <YStack className="overflow-hidden" gap="md" p="md" style={{ height: MODAL_HEIGHT }}>
      <YStack className="min-h-0 flex-1">
        <MapRenderer gestureHandling="greedy" height="100%" locations={locations} onMarkerClick={onMarkerClick} />
      </YStack>
    </YStack>
  </Modal>
);

const ExpandIcon = () => (
  <svg fill="none" height="16" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" width="16">
    <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const GoogleMapWidget = ({ locations: rawLocations }: GoogleMapWidgetProps) => {
  const locations = useMemo(() => {
    const invalid = rawLocations.filter((loc) => loc.lat == null || loc.lng == null);
    if (invalid.length > 0) {
      captureSentryException(new Error(`Map locations missing coordinates: ${JSON.stringify(invalid)}`));
    }
    return rawLocations.filter((loc): loc is ValidMapLocation => loc.lat != null && loc.lng != null);
  }, [rawLocations]);

  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isDetailLoading, setIsDetailLoading] = useState(false);
  const [placeDetails, setPlaceDetails] = useState<PlaceDetails | null>(null);
  const detailRequestRef = useRef(0);

  const handleExpand = useCallback(() => setIsMapModalOpen(true), []);
  const handleCloseMap = useCallback(() => setIsMapModalOpen(false), []);
  const handleCloseDetail = useCallback(() => setIsDetailModalOpen(false), []);

  const handleMarkerClick = useCallback((location: MapLocation, map: google.maps.Map) => {
    /* eslint-disable fp/no-mutation */
    const requestId = ++detailRequestRef.current;
    /* eslint-enable fp/no-mutation */
    setIsDetailLoading(true);
    setPlaceDetails(null);
    setIsDetailModalOpen(true);

    fetchPlaceDetails(map, location)
      .then((details) => {
        if (requestId !== detailRequestRef.current) return;
        setPlaceDetails(details);
        setIsDetailLoading(false);
      })
      .catch(() => {
        if (requestId !== detailRequestRef.current) return;
        setPlaceDetails(null);
        setIsDetailLoading(false);
      });
  }, []);

  if (locations.length === 0) return null;

  return (
    <YStack className="overflow-hidden rounded-lg border border-neutral-200">
      <XStack alignItems="center" className="px-md py-sm" justifyContent="between">
        <Text color="secondary" type="p-sm">
          Locations
        </Text>
        <button
          className="flex size-7 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
          onClick={handleExpand}
          title="Expand map"
          type="button"
        >
          <ExpandIcon />
        </button>
      </XStack>

      {!isMapModalOpen ? (
        <YStack className="px-sm pb-sm">
          <MapRenderer height={COMPACT_HEIGHT} locations={locations} onMarkerClick={handleMarkerClick} />
        </YStack>
      ) : null}

      <GoogleMapModal
        isOpen={isMapModalOpen}
        locations={locations}
        onClose={handleCloseMap}
        onMarkerClick={handleMarkerClick}
      />

      <PlaceDetailModal
        details={placeDetails}
        isLoading={isDetailLoading}
        isOpen={isDetailModalOpen}
        onClose={handleCloseDetail}
      />
    </YStack>
  );
};
